// Schedule options for automated reminders (ClickUp 868jmp2ge).
// Reuses the scheduled-reports timezone kit so the two features present
// timezones identically; only the time granularity differs (reminders
// allow 15-minute increments, reports are hourly).

import {
    TIMEZONE_OPTIONS,
    defaultTimezone,
    tzAbbrev,
} from './scheduledReports.constants';

export { TIMEZONE_OPTIONS, defaultTimezone, tzAbbrev };

// '00:00'..'23:45' in 15-minute steps, labeled in 12h ("9:15 AM").
export const SEND_TIME_OPTIONS: { label: string; value: string }[] =
    Array.from({ length: 24 * 4 }, (_, i) => {
        const h = Math.floor(i / 4);
        const m = (i % 4) * 15;
        const ampm = h < 12 ? 'AM' : 'PM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return {
            label: `${h12}:${String(m).padStart(2, '0')} ${ampm}`,
            value: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        };
    });

const SEND_TIME_LABELS: Record<string, string> = Object.fromEntries(
    SEND_TIME_OPTIONS.map((o) => [o.value, o.label]),
);

// The schedule a legacy (unscheduled) reminder effectively runs on today,
// mirroring pom-2-api's sweep gating exactly (see its backfill script —
// the 8am sweep fires all CHARGING/UPDATE plus SC 24/48h; the 8pm sweep
// fires only intervalHours=12). Null = matches neither sweep (dormant).
export const legacySendTimesFor = (
    reminderType?: string | null,
    intervalHours?: number | null,
): string[] | null => {
    if (reminderType === 'CHARGING' || reminderType === 'UPDATE_REMINDER') {
        return intervalHours === 12 ? ['08:00', '20:00'] : ['08:00'];
    }
    if (reminderType === 'STAY_CONNECTED') {
        if (intervalHours === 12) return ['20:00'];
        if (intervalHours === 24 || intervalHours === 48) return ['08:00'];
        return null;
    }
    return null;
};

// "8:00 AM & 8:00 PM ET" — for reminder cards and summaries. Legacy
// reminders (no schedule yet) fall back to describing the fixed cycle
// they actually run on today, per reminder type. perRecipient schedules
// (ClickUp 868kpzqcd) say so, with the fixed zone shown as the fallback.
export const describeSchedule = (
    sendTimes?: string[] | null,
    timezone?: string | null,
    reminderType?: string | null,
    intervalHours?: number | null,
    timezoneStrategy?: string | null,
): string => {
    const label = (times: string[]) => times.map((t) => SEND_TIME_LABELS[t] ?? t).join(' & ');
    if (sendTimes?.length && timezone) {
        if (timezoneStrategy === 'perRecipient') {
            return `${label(sendTimes)} in each user's local time (${tzAbbrev(timezone)} fallback)`.trim();
        }
        return `${label(sendTimes)} ${tzAbbrev(timezone)}`.trim();
    }
    const legacy = legacySendTimesFor(reminderType, intervalHours);
    if (!legacy) return 'Not scheduled (legacy configuration)';
    return `${label(legacy)} ET (system default)`;
};
